import { useRouter } from 'next/router';

const forceArray = value => (Array.isArray(value) ? value : [value]);

const getShopifyFilterValue = (filters, key, value) =>
  (filters.find(f => f.key === key).values || []).find(({ label }) => label === value);

const useCollectionQuery = () => {
  const {
    query,
    asPath,
    query: { s },
    query: { a, b, slug, ...queryWithoutPagination },
  } = useRouter();

  // Converts query object to URL, used to comply with rewrite rules in middleware
  const toUrl = q =>
    `${asPath.split('?')[0]}?${new URLSearchParams(
      Object.entries(q)
        .map(([name, values]) => [name, [].concat(values)])
        // Multiple values for same key instead of comma separated
        .reduce((acc, [name, values]) => acc.concat(values.map(x => [name, x])), [])
    ).toString()}`
      // Remove empty query params
      .split('?')
      .filter(Boolean)
      .join('?');

  const getFilterValues = filters =>
    Object.entries(query)
      .map(([key, value]) => [key, value])
      .filter(([key]) => filters.find(f => f.key === key))
      .reduce((acc, [key, label]) => ({ ...acc, [key]: forceArray(label) }), {});

  // Gets Shopify filter inputs from current query values (filters contain input JSON)
  const getShopifyFilterInputs = filters =>
    Object.entries(getFilterValues(filters))
      .sort(([keyA], [keyB]) => (keyA > keyB ? 1 : -1))
      .reduce((acc, [key, labels]) => {
        if (key === 'f.price')
          return [...acc, { price: { min: parseFloat(labels[0]), max: parseFloat(labels[1]) } }];

        return [
          ...acc,
          ...labels
            .filter(label => getShopifyFilterValue(filters, key, label))
            .map(label => JSON.parse(getShopifyFilterValue(filters, key, label).input)),
        ];
      }, []);

  const isFilterAvailable = (availableFilters, key, label) =>
    getFilterValues(availableFilters)[key]?.includes(label) ||
    getShopifyFilterValue(availableFilters, key, label)?.count > 0;

  const getResetUrl = filters =>
    toUrl(
      Object.fromEntries(
        Object.entries(queryWithoutPagination).filter(([key]) => !filters.find(f => f.key === key))
      )
    );

  const getFilterUrl = (key, value) => {
    const { [key]: v, ...queryWithoutFilter } = queryWithoutPagination;

    const currentValue = v ? forceArray(v) : [];

    // Remove from array if multiple
    if (currentValue.includes(value))
      return toUrl({ ...queryWithoutFilter, [key]: currentValue.filter(k => k !== value) });

    return toUrl({
      ...queryWithoutFilter,
      [key]: [...currentValue, value],
    });
  };

  const getRangeFilterUrl = (key, values) => {
    const { [key]: v, ...queryWithoutFilter } = queryWithoutPagination;

    return toUrl({ ...queryWithoutFilter, [key]: values });
  };

  const getSortUrl = sort => toUrl({ ...queryWithoutPagination, s: sort });
  const getBeforeUrl = before => toUrl({ ...queryWithoutPagination, b: before });
  const getAfterUrl = after => toUrl({ ...queryWithoutPagination, a: after });

  return {
    sort: s,
    before: b,
    after: a,
    getFilterValues,
    getShopifyFilterInputs,
    isFilterAvailable,
    getResetUrl,
    getFilterUrl,
    getRangeFilterUrl,
    getSortUrl,
    getBeforeUrl,
    getAfterUrl,
  };
};

export default useCollectionQuery;
